import { FC } from "react";
import {
  Box,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";

import {
  ClaimPayer,
  ManualPaymentFields,
  ManualPaymentOutcome,
  PayerTier,
} from "../../../../../types/claims";
import CoreDatePicker from "../../../../ui/core/CoreDatePicker";
import CoreInput from "../../../../ui/core/CoreInput";
import CoreSelect from "../../../../ui/core/CoreSelect";
import { MAX_CLAIM_DOCUMENT_MB, payerOptions } from "../claimAppeals";
import { ManualPaymentFieldErrors, OUTCOME_LABELS } from "./manualPayments";
import {
  FieldMessage,
  PdfDropzone,
  RuleItem,
  RulesList,
} from "./ClaimEditDialogParts";

const moneyInputSlotProps = (ariaLabel: string) => ({
  input: {
    startAdornment: <InputAdornment position="start">$</InputAdornment>,
  },
  htmlInput: { inputMode: "decimal" as const, "aria-label": ariaLabel },
});

interface RecordPaymentFormProps {
  fields: ManualPaymentFields;
  /** Errors shown under each field (live, server and document). */
  fieldErrors: ManualPaymentFieldErrors;
  /** Live validation, always on; drives the rules checklist. */
  liveErrors: ManualPaymentFieldErrors;
  primaryRemit: boolean;
  today: string;
  document: File | null;
  payers?: ClaimPayer[];
  onOutcomeChange: (outcome: ManualPaymentOutcome) => void;
  onPayerTierChange: (tier: PayerTier) => void;
  onFieldChange: <K extends keyof ManualPaymentFields>(
    key: K,
    value: ManualPaymentFields[K],
  ) => void;
  onFiles: (files: File[]) => void;
  onRemoveDocument: () => void;
}

/** The first step of RecordPaymentDialog: the payment fields and letter. */
const RecordPaymentForm: FC<RecordPaymentFormProps> = ({
  fields,
  fieldErrors,
  liveErrors,
  primaryRemit,
  today,
  document,
  payers,
  onOutcomeChange,
  onPayerTierChange,
  onFieldChange,
  onFiles,
  onRemoveDocument,
}) => {
  const isPaid = fields.outcome === "paid";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 2 }}>
      <RulesList label="Payment rules">
        <RuleItem met={!liveErrors.amount}>
          {isPaid
            ? "Paid: the amount must be more than $0."
            : "Denied: the amount is $0."}
        </RuleItem>
        {!primaryRemit && (
          <RuleItem met={!liveErrors.payerTier}>
            The claim has no primary remit, so only a primary payment can be
            recorded.
          </RuleItem>
        )}
        <RuleItem>
          You'll see a summary before anything is saved. A saved payment can't
          be edited.
        </RuleItem>
      </RulesList>

      <Box>
        <Typography
          component="label"
          sx={{ fontWeight: 700, fontSize: "0.875rem", display: "block" }}
        >
          Outcome
        </Typography>
        <RadioGroup
          row
          aria-label="Outcome"
          value={fields.outcome}
          onChange={(e) =>
            onOutcomeChange(e.target.value as ManualPaymentOutcome)
          }
        >
          {(Object.keys(OUTCOME_LABELS) as ManualPaymentOutcome[]).map(
            (outcome) => (
              <FormControlLabel
                key={outcome}
                value={outcome}
                control={<Radio size="small" />}
                label={OUTCOME_LABELS[outcome]}
              />
            ),
          )}
        </RadioGroup>
        <FieldMessage error={fieldErrors.outcome}>
          {isPaid
            ? "The payer paid the claim."
            : "The payer upheld the denial."}
        </FieldMessage>
      </Box>

      <Box>
        <CoreSelect
          label="Payer level"
          variant="outlined"
          value={fields.payerTier}
          options={payerOptions(payers)}
          onChange={(e) => onPayerTierChange(e.target.value as PayerTier)}
          inputProps={{ "aria-label": "Payer level" }}
        />
        <FieldMessage error={fieldErrors.payerTier}>
          Which payer sent the letter.
        </FieldMessage>
      </Box>

      <Box>
        <CoreInput
          label="Amount"
          value={fields.amount}
          type="number"
          onChange={(e) => onFieldChange("amount", e.target.value)}
          disabled={!isPaid}
          placeholder="0"
          slotProps={moneyInputSlotProps("Amount")}
        />
        <FieldMessage error={fieldErrors.amount}>
          {isPaid ? "What the payer paid." : "A denial is always $0."}
        </FieldMessage>
      </Box>

      <Box>
        <CoreDatePicker
          label={isPaid ? "Date paid" : "Date denied"}
          value={fields.paymentDate}
          onChange={(value) => onFieldChange("paymentDate", value)}
          disableFuture
          maxDate={dayjs(today)}
        />
        <FieldMessage error={fieldErrors.paymentDate}>
          As shown on the letter. Not in the future.
        </FieldMessage>
      </Box>

      <Box>
        <CoreInput
          label="Payer name"
          value={fields.payerName}
          onChange={(e) => onFieldChange("payerName", e.target.value)}
          slotProps={{ htmlInput: { "aria-label": "Payer name" } }}
        />
        <FieldMessage error={fieldErrors.payerName}>
          Pre-filled with the payer for this level; change it if another payer
          sent the letter.
        </FieldMessage>
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flex: "1 1 200px" }}>
          <CoreInput
            label="Check / EFT # (optional)"
            value={fields.checkOrEftNumber}
            onChange={(e) => onFieldChange("checkOrEftNumber", e.target.value)}
            slotProps={{ htmlInput: { "aria-label": "Check / EFT #" } }}
          />
          <FieldMessage error={fieldErrors.checkOrEftNumber}>
            Leave empty if the letter doesn't show it.
          </FieldMessage>
        </Box>
        <Box sx={{ flex: "1 1 200px" }}>
          <CoreInput
            label="Payer claim # / ICN (optional)"
            value={fields.payerClaimNumber}
            onChange={(e) => onFieldChange("payerClaimNumber", e.target.value)}
            slotProps={{ htmlInput: { "aria-label": "Payer claim # (ICN)" } }}
          />
          <FieldMessage error={fieldErrors.payerClaimNumber}>
            Leave empty if the letter doesn't show it.
          </FieldMessage>
        </Box>
      </Box>

      {isPaid && (
        <Box>
          <CoreInput
            label="Patient responsibility (optional)"
            value={fields.patientResponsibility}
            onChange={(e) =>
              onFieldChange("patientResponsibility", e.target.value)
            }
            type="number"
            placeholder="0.00"
            slotProps={moneyInputSlotProps("Patient responsibility")}
          />
          <FieldMessage error={fieldErrors.patientResponsibility}>
            What the letter leaves to the patient, if anything.
          </FieldMessage>
        </Box>
      )}

      <Box>
        <PdfDropzone
          label="Correspondence (optional)"
          file={document}
          onFiles={onFiles}
          onRemove={onRemoveDocument}
          hasError={!!fieldErrors.document}
          inputTestId="payment-document-input"
        />
        <FieldMessage error={fieldErrors.document}>
          The payer's letter: one PDF, up to {MAX_CLAIM_DOCUMENT_MB} MB.
        </FieldMessage>
      </Box>
    </Box>
  );
};

export default RecordPaymentForm;
